




















































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import getProgressAndStatus from "@/components/app/admin/automation/getProgressAndStatus";
import { FilterOperators, FilterTypes, Sorters } from "@/data/table";
import { ConfirmModalModes } from "@/data/enums";
import i18n from "@/i18n";
import { IBulkAction } from "@/models/bulkactions";
import { UpmindObjectTypes } from "@/data/enums/objects";

@Component
export default class BulkActionsTable extends Mixins(
  FilteringSortingTableStore
) {
  // Props
  @Prop({ required: true })
  public objectType!: UpmindObjectTypes;

  @Prop({ default: "tr" })
  public rowTag!: string;

  @Prop({ default: () => () => ({}) })
  public rowProps!: (bulkAction: IBulkAction) => Record<string, string>;

  @Prop({ default: () => () => ({}) })
  public rowEvents!: (bulkAction: IBulkAction) => Record<string, Function>;

  // Data

  public dataModule = DataModules.BULK_ACTIONS;

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "status,-completed_at,started_at,-created_at",
      direction: Sorters.ASCENDING
    }
  };

  public sorters: ISorter[] = [
    {
      field: "status,-completed_at,started_at,-created_at",
      label: i18n.t("_._default")
    },
    {
      field: "name",
      label: i18n.t("_.name")
    },
    {
      field: "started_at",
      label: i18n.t("_.start_date")
    },
    {
      field: "status",
      label: i18n.t("_.status")
    }
  ];

  public filters: IFilter[] = [
    {
      key: "name",
      label: this.$t("_.name"),
      type: FilterTypes.STRING,
      operator: FilterOperators.CONTAINS
    },
    {
      key: "started_at",
      label: this.$t("_.start_date"),
      type: FilterTypes.DATE,
      operator: FilterOperators.AFTER
    },
    {
      key: "status.code",
      label: this.$t("_.status"),
      type: FilterTypes.SELECT_CHECKBOX,
      options: [
        { value: "bulk_action_draft", label: i18n.t("_.draft") },
        {
          value: "bulk_action_complete&&bulk_action_incomplete",
          label: i18n.t("_.complete")
        },
        { value: "bulk_action_scheduled", label: i18n.t("_.scheduled") },
        { value: "bulk_action_in_progress", label: i18n.t("_.in_progress") },
        { value: "bulk_action_cancelled", label: i18n.t("_.cancelled") }
      ],
      operator: FilterOperators.EQUAL
    }
  ];

  public async created() {
    if (!this.contentIsAvailable) return;
    this.addSegmentFilter();
    this.apiProps = { with: "status,segment" };
    this.getData();
  }

  public guardFunctionalities = ["list_bulk_actions"];

  // Getters
  public get objectTypeFilter() {
    return { "filter[object_type]": this.objectType };
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get cogItems() {
    return [
      {
        visible: () => this.$userCan("reschedule_bulk_action"),
        label: this.$t(`_action.duplicate`),
        action: this.rescheduleBulkAction
      },
      {
        visible: row =>
          this.$userCan("cancel_bulk_action") && this.actionIsCancellable(row),
        label: this.$t(`_action.cancel`),
        action: this.cancelBulkAction,
        loading: row => row.cancelling
      },
      {
        visible: row =>
          this.$userCan("retry_bulk_action") && this.actionIsRetryable(row),
        label: this.$t(`_action.retry`),
        action: this.retryBulkAction
      },
      {
        visible: row =>
          this.$userCan("delete_bulk_action") && !this.isActionInProgress(row),
        label: this.$t(`_action.delete`),
        action: this.deleteConfirmBulkAction,
        class: "has-text-danger"
      }
    ];
  }

  public get canViewBulkAction() {
    return this.$userCan("get_bulk_action");
  }

  // Methods

  public async addSegmentFilter() {
    const typeFilter =
      this.objectType === UpmindObjectTypes.CONTRACTS_PRODUCT
        ? { "filter[type|like]": `${UpmindObjectTypes.CONTRACTS_PRODUCT}%` }
        : this.objectType === UpmindObjectTypes.CLIENT
        ? { "filter[type]": UpmindObjectTypes.CLIENT }
        : {};

    const segments = await this.$store.dispatch(
      `data/${DataModules.SEGMENTS}/list`,
      {
        vm: this,
        scope: this.dataScope,
        storeData: false,
        params: {
          limit: 0,
          ...typeFilter
        }
      }
    );

    const options = this.$_.map(segments, segment => {
      return { id: segment.id, label: segment.name };
    });

    this.filters.push({
      key: "segment.id",
      label: i18n.t("_.segment"),
      type: FilterTypes.SELECT_MULTI,
      value: [],
      options: options,
      operator: FilterOperators.EQUAL
    });
  }
  public getStoreParams(payload: any): Promise<any> {
    return this.$_.merge(payload, {
      storeModule: this.dataModule,
      ignoreStored: true,
      params: {
        ...this.objectTypeFilter
      }
    });
  }

  public canScheduleBulkAction(bulkAction) {
    return (
      bulkAction.status.code === "bulk_action_draft" &&
      this.$userCan("schedule_bulk_action")
    );
  }

  public isBulkActionDraf(action) {
    return action.status.code === "bulk_action_draft";
  }

  public getProgressAndStatus(bulkAction) {
    return getProgressAndStatus(bulkAction);
  }

  public actionIsCancellable(action) {
    return ["bulk_action_scheduled", "bulk_action_in_progress"].includes(
      action.status.code
    );
  }

  public actionIsRetryable(action) {
    return ["bulk_action_incomplete", "bulk_action_cancelled"].includes(
      action.status.code
    );
  }

  public isActionInProgress(action) {
    return ["bulk_action_in_progress"].includes(action.status.code);
  }

  public cancelBulkAction(action) {
    this.$set(action, "cancelling", true);

    this.$store
      .dispatch(`data/${DataModules.BULK_ACTIONS}/cancel`, {
        vm: this,
        id: action.id,
        scope: this.dataScope,
        updateStore: true
      })
      .then(() => {
        this.$delete(action, "cancelling");
      })
      .catch(error => {
        this.$delete(action, "cancelling");
        this.$store.dispatch("api/handleError", error);
      });
  }

  public rescheduleBulkAction(action) {
    this.$store.dispatch("ui/open/modal", {
      config: {
        width: 480,
        component: () =>
          import(
            "@/components/app/admin/automation/bulkActionRescheduleModal.vue"
          ),
        props: {
          bulkAction: action
        },
        events: {
          success: () => {
            this.getData({ ignoreStored: true });
          }
        }
      }
    });
  }

  public scheduleBulkAction(action) {
    this.$store.dispatch("ui/open/modal", {
      config: {
        width: 580,
        component: () =>
          import(
            "@/components/app/admin/automation/bulkActionScheduleModal.vue"
          ),
        props: {
          bulkAction: action,
          payload: {
            scope: this.dataScope,
            updateStore: true
          }
        }
      }
    });
  }

  public retryBulkAction(action) {
    this.$store
      .dispatch(`data/${DataModules.BULK_ACTIONS}/retry`, {
        vm: this,
        id: action.id,
        scope: this.dataScope,
        updateStore: true
      })
      .then(() => {
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.bulk_action_scheduled")
        });
      })
      .catch(error => this.$store.dispatch("api/handleError", error));
  }

  public async deleteConfirmBulkAction(action) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: this.isBulkActionDraf(action)
            ? ConfirmModalModes.BUTTON
            : ConfirmModalModes.PIN,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_bulk_action_are_you_sure")
        },
        events: {
          confirmed: async () => {
            await this.deleteBulkAction(action);
            confirm.close();
          }
        }
      }
    });
  }

  public deleteBulkAction(action) {
    return this.$store
      .dispatch(`data/${DataModules.BULK_ACTIONS}/remove`, {
        vm: this,
        id: action.id,
        scope: this.dataScope,
        updateStore: true
      })
      .then(() => {
        this.getData({ ignoreStored: true });
        this.$store.dispatch("toast/show", {
          message: this.$t("_.object_deleted", {
            object: this.$t("_.bulk_action")
          })
        });
      })
      .catch(error => this.$store.dispatch("api/handleError", error));
  }

  public openSegmentResultsModal(bulkAction) {
    if (!bulkAction.segment) return;
    this.$store.dispatch("ui/open/slideModal", {
      config: {
        component: () =>
          import("@/components/app/admin/segments/segmentResultsModal.vue"),
        props: {
          segmentId: bulkAction.segment_id,
          segmentType: this.$_.get(bulkAction, "segment.type", "")
        }
      }
    });
  }

  public startingMessage(action) {
    if (action.status.code === "bulk_action_draft") {
      return { label: "--", showDate: false };
    }

    if (
      action.status.code === "bulk_action_scheduled" &&
      this.$moment(action.started_at)
        .local()
        .isBefore(new Date())
    ) {
      return { label: this.$t("_sentence.starting_soon"), showDate: false };
    }
    return this.$moment(action.started_at)
      .local()
      .isAfter(new Date())
      ? { label: "Starts", showDate: true }
      : { label: "Started", showDate: true };
  }
}
